/* eslint-disable react/jsx-props-no-spreading */
import * as React from 'react';
import './ArrayEditor.less';
import Text from './Paragraph';
import Question from './Question';
import Statement from './Statement';
import HiddenField from './HiddenField';
import DataCaptureField from './DataCaptureField';
import Workflow from '../../../models/be/Workflow';
import { dbsChecks } from '../../../core/constants';
import AddressLookupField from './AddressLookupField';
import NameChangeDocuments from './NameChangeDocuments';
import PrimaryName from '../../../models/be/PrimaryName';
// eslint-disable-next-line import/no-cycle
import AddressHistoryEditor from './AddressHistoryEditor';
// eslint-disable-next-line import/no-cycle
import AdditionalNamesEditor from './AdditionalNamesEditor';
import { Component, FormSpecificationPage } from '../../../models/be/db/FormSpecification';
import UkDriverLicenceNumberField from './UkDriverLicenceNumberField';
import PrimaryNameChangeDocuments from './PrimaryNameChangeDocuments';
import AdditionalNameDatesValidator from './AdditionalNameDatesValidator';
import IdVerificationTypeSelection from '../../../commonComponents/fields/IdVerificationTypeSelection';

const DataCaptureComponent = (props: Props) => {
	const { component, candidateId, workflowPrimaryName, customerUserName } = props;
	const statementProps = {
		candidateId,
		workflowPrimaryName,
		customerUserName,
		workflowTypeCode: props.visibleWorkflowFields?.workflowTypeCode,
		category: props.visibleWorkflowFields?.category,
		workflowId: props.visibleWorkflowFields?.id ?? props.workflowId
	};

	switch (component.name) {
		case null:
		case undefined:
		case 'DataCaptureField':
			return (
				<DataCaptureField
					workflowId={props.visibleWorkflowFields?.id ?? props.workflowId}
					candidateId={props.visibleWorkflowFields?.candidateId ?? props.candidateId}
					{...component}
					stripe={props.stripe}
					fileIsDownloadable={props.fileIsDownloadable}
					visibleWorkflowFields={props.visibleWorkflowFields}
					onFileUpload={props.onFileUpload}
					setSubmitButtonsDisabled={props.setSubmitButtonsDisabled}
					setBackButtonsDisabled={props.setBackButtonsDisabled}
					customerName={props.customerName}
					isToDateReadOnly={props.isToDateReadOnly}
					customerCountryCode={props.customerCountryCode}
				/>
			);
		case 'Paragraph':
			return (
				<Text
					texts={component.props.texts}
					className={component.props.className}
					visibleWorkflowFields={props.visibleWorkflowFields}
					workflowTypeCode={props.visibleWorkflowFields?.workflowTypeCode}
					style={component.props.style}
					paragraphType={component.props.paragraphType}
				/>
			);
		case 'Statement':
			return <Statement {...statementProps} {...component} />;
		case 'Question':
			return <Question {...component} />;
		case 'AdditionalNamesEditor':
			return (
				<AdditionalNamesEditor
					{...component}
					primaryName={workflowPrimaryName}
					setSubmitButtonsDisabled={props.setSubmitButtonsDisabled}
					setBackButtonsDisabled={props.setBackButtonsDisabled}
					workflowTypeCode={props.visibleWorkflowFields.workflowTypeCode}
					workflowId={props.workflowId}
				/>
			);
		case 'AddressHistoryEditor':
			return (
				<AddressHistoryEditor
					fieldNames={component.fieldNames}
					components={component.props.components}
					hasCheckCountry={component.props.hasCheckCountry}
					visibleWorkflowFields={props.visibleWorkflowFields}
					countryRestriction={component.props.countryRestriction}
					numberOfYearsToCover={component.props.numberOfYearsToCover}
					setSubmitButtonsDisabled={props.setSubmitButtonsDisabled}
					setBackButtonsDisabled={props.setBackButtonsDisabled}
				/>
			);
		case 'NameChangeDocuments':
			return dbsChecks.includes(props.visibleWorkflowFields.workflowTypeCode) ? (
				<PrimaryNameChangeDocuments
					{...component}
					visibleWorkflowFields={props.visibleWorkflowFields}
					navigateToPage={props.navigateToPage}
					stripe={props.stripe}
				/>
			) : (
				<NameChangeDocuments
					{...component}
					visibleWorkflowFields={props.visibleWorkflowFields}
					navigateToPage={props.navigateToPage}
					stripe={props.stripe}
					formSpecificationPage={props.formSpecificationPage}
				/>
			);
		case 'AdditionalNameDatesValidator':
			return <AdditionalNameDatesValidator visibleWorkflowFields={props.visibleWorkflowFields} />;
		case 'BirthDatesMatchValidator':
			return null;
		case 'AddressLookup':
			return (
				<AddressLookupField
					baseFieldName={component.props.baseFieldName}
					countryRestriction={component.props.countryRestriction}
				/>
			);
		case 'HiddenField':
			return <HiddenField fieldNames={component.fieldNames} value={component.props.value} />;
		case 'UkDriverLicenceNumber':
			return <UkDriverLicenceNumberField component={component} visibleWorkflowFields={props.visibleWorkflowFields} />;
		case 'FormValidation':
			return null;
		case 'IdVerificationTypeSelection':
			return (
				<IdVerificationTypeSelection
					component={component}
					workflow={props.visibleWorkflowFields}
					setSubmitButtonsDisabled={props.setSubmitButtonsDisabled}
					setBackButtonsDisabled={props.setBackButtonsDisabled}
				/>
			);
		default:
			throw new Error(`Unknown component name: ${component.name}.`);
	}
};

interface Props {
	component: Component;
	candidateId?: string;
	workflowId?: string;
	visibleWorkflowFields?: Workflow;
	navigateToPage?: (pageSlug: string) => void;
	stripe?: stripe.Stripe;
	setSubmitButtonsDisabled?: (disabled: boolean) => void;
	setBackButtonsDisabled?: (disabled: boolean) => void;
	workflowPrimaryName?: PrimaryName;
	customerUserName?: string;
	fileIsDownloadable?: boolean;
	onFileUpload?: () => void;
	customerName: string;
	isToDateReadOnly?: boolean;
	formSpecificationPage?: FormSpecificationPage;
	customerCountryCode?: string;
}

DataCaptureComponent.defaultProps = {
	candidateId: null,
	workflowId: null,
	visibleWorkflowFields: null,
	navigateToPage: null,
	stripe: null,
	setSubmitButtonsDisabled: null,
	setBackButtonsDisabled: null,
	workflowPrimaryName: null,
	customerUserName: null,
	fileIsDownloadable: false,
	onFileUpload: null,
	formSpecificationPage: null,
	customerCountryCode: null
};

export default DataCaptureComponent;
